import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import type { BoardRow } from '@shared/types';
import { EmptyState, ErrorState, LoadingBar } from '@web/components/Feedback';
import { LiveRegion, useThrottledAnnouncement } from '@web/components/LiveRegion';
import { useIsCompact } from '@web/hooks/useMediaQuery';
import { BoardCard, BoardTableRow } from './BoardRow';
import { PeekDrawer } from './PeekDrawer';
import { SummaryStrip, type BoardFilter } from './SummaryStrip';
import { useBoard, useResolvedToday } from './useBoard';
import { useStableOrder } from './useStableOrder';

const COLUMNS = [
  { key: 'state', label: 'State' },
  { key: 'candidate', label: 'Candidate' },
  { key: 'message', label: 'Last message' },
  { key: 'idle', label: 'Idle' },
  { key: 'temp', label: 'Temp' },
  { key: 'attn', label: 'Attn' },
  { key: 'flags', label: 'Flags' },
  { key: 'owner', label: 'Owner' },
];

export function BoardPage() {
  const board = useBoard();
  const resolved = useResolvedToday();
  const compact = useIsCompact();

  const [filter, setFilter] = useState<BoardFilter>(null);
  const [search, setSearch] = useState('');
  const [peeked, setPeeked] = useState<string | null>(null);

  // Deferred so a keystroke never janks against a socket flush: React keeps the
  // old list on screen and restarts the filtered render if a newer value lands.
  const query = useDeferredValue(search).trim().toLowerCase();

  const rows = useMemo(() => board.data ?? [], [board.data]);
  const { order, staleOrder, sortNow, containerProps } = useStableOrder(rows);

  const counts = useMemo(
    () => ({
      ai: rows.filter((row) => row.handlingState === 'ai').length,
      parked: rows.filter((row) => row.handlingState === 'parked').length,
      human: rows.filter((row) => row.handlingState === 'human').length,
      resolved: resolved.data?.count ?? 0,
    }),
    [rows, resolved.data?.count],
  );

  const visible = useMemo(() => {
    if (filter === 'resolved') return resolved.data?.rows ?? [];

    const byId = new Map(rows.map((row) => [row.conversationId, row]));
    const ordered = order
      .map((id) => byId.get(id))
      .filter((row): row is BoardRow => row !== undefined);

    return ordered.filter((row) => {
      if (filter !== null && row.handlingState !== filter) return false;
      if (query === '') return true;
      return (
        row.candidateName.toLowerCase().includes(query) ||
        (row.trade ?? '').toLowerCase().includes(query) ||
        row.phone.includes(query) ||
        (row.lastMessagePreview ?? '').toLowerCase().includes(query)
      );
    });
  }, [filter, order, rows, query, resolved.data?.rows]);

  // A row is "new" only the first time it is seen, and never on the first load
  // — otherwise every row on the board flashes when the app opens.
  const seen = useRef<Set<string> | null>(null);
  const arrivals = new Set<string>();
  if (seen.current === null) {
    if (board.data !== undefined) seen.current = new Set(rows.map((row) => row.conversationId));
  } else {
    for (const row of rows) {
      if (!seen.current.has(row.conversationId)) arrivals.add(row.conversationId);
    }
  }
  useEffect(() => {
    if (seen.current === null) return;
    for (const row of rows) seen.current.add(row.conversationId);
  }, [rows]);

  const announcement =
    counts.parked === 0
      ? 'Queue clear.'
      : `${String(counts.parked)} conversation${counts.parked === 1 ? '' : 's'} waiting for a human.`;
  const announced = useThrottledAnnouncement(announcement, 8000);

  if (board.isPending) return <LoadingBar label="Loading board" />;
  if (board.error !== null) {
    return (
      <ErrorState
        error={board.error}
        onRetry={() => {
          void board.refetch();
        }}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <LiveRegion text={announced} />

      <SummaryStrip
        counts={counts}
        active={filter}
        onChange={setFilter}
        resolvedCapped={resolved.data?.capped ?? false}
      />

      <div className="flex items-center gap-2 border-b border-rule px-3 py-1.5">
        <label className="sr-only" htmlFor="board-search">
          Filter conversations
        </label>
        <input
          id="board-search"
          type="search"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
          placeholder="Filter by name, trade, number or message"
          className="h-8 min-w-0 flex-1 border border-rule bg-panel px-2 text-ink placeholder:text-ink-faint"
        />
        <span className="font-mono text-[11px] text-ink-faint tabular-nums">
          {visible.length}/{rows.length}
        </span>
        {staleOrder && (
          <button
            type="button"
            onClick={sortNow}
            className="flex h-8 items-center gap-2 border border-warm px-2 font-mono text-[10px] tracking-[0.08em] text-warm"
          >
            <span aria-hidden className="inline-block size-[6px] bg-warm" />
            ORDER CHANGED · SORT NOW
          </button>
        )}
      </div>

      <div
        {...containerProps}
        className="min-h-0 flex-1 overflow-auto [overscroll-behavior:contain]"
      >
        {visible.length === 0 ? (
          <EmptyState
            headline={
              rows.length === 0 ? 'No live conversations' : 'Nothing matches the current filter'
            }
            detail={
              rows.length === 0
                ? 'Inbound messages appear here the moment they arrive.'
                : 'Clear the filter or the search box to see the rest of the board.'
            }
          />
        ) : compact ? (
          <div>
            {visible.map((row) => (
              <BoardCard
                key={row.conversationId}
                row={row}
                selected={peeked === row.conversationId}
                isNew={arrivals.has(row.conversationId)}
                onOpen={setPeeked}
              />
            ))}
          </div>
        ) : (
          <table className="w-full border-collapse text-left">
            <caption className="sr-only">
              Active conversations, sorted by attention. Order is held while the pointer is over the
              table.
            </caption>
            <thead className="sticky top-0 z-10 bg-panel">
              <tr className="h-7 border-b border-rule-strong">
                {COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    className="px-2 font-mono text-[10px] font-medium tracking-[0.1em] text-ink-faint uppercase"
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <BoardTableRow
                  key={row.conversationId}
                  row={row}
                  selected={peeked === row.conversationId}
                  isNew={arrivals.has(row.conversationId)}
                  onOpen={setPeeked}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <PeekDrawer
        conversationId={peeked}
        onClose={() => {
          setPeeked(null);
        }}
      />
    </div>
  );
}
